// Analytics tab. Two sub-tabs since 2026-09-10 (owner): **Dashboard**, which is
// the content this tab already carried, and **Reporting**, the criteria form
// that produces a PDF.
//
// THE TABS ARE IN THE HEADER and the page opens with a one-line intro (owner,
// 2026-09-28): the sidebar names the section, the selected header tab names the
// page, and there is no title (`components/PageTabs.tsx`). `DashboardScreen.tsx`
// renders no header of its own for the same reason.
//
// THE INTRO SWAPS WITH THE SELECTED TAB. The two sentences describe genuinely
// different jobs — one reads a dashboard, the other exports a collection — so a
// single sentence above both would misdescribe whichever tab was open.
//
// WHAT DOES NOT SWAP IS THE FILTER SET (owner, 2026-09-15). Both tabs ask the
// same fifteen questions, so they share one answer, held here: narrow the
// Dashboard, switch to Reporting, and the form is already filled in.
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Button, Tooltip } from '@fluentui/react-components'
import { ArrowClockwiseRegular } from '@fluentui/react-icons'
import { setAnalyticsLens } from '../api/lens'
import { DashboardFilterButton } from '../components/DashboardFilterButton'
import { PageTabs } from '../components/PageTabs'
import { ScreenActions } from '../components/ScreenActions'
import { ScreenIntro } from '../components/ScreenIntro'
import { emptyCriteria, type Criteria } from '../lib/criteria'
import type { Invoice } from '../lib/types'
import {
  ANALYTICS_SECTIONS,
  ANALYTICS_SECTION_LABEL,
  ANALYTICS_SECTION_SUBHEADER,
  type AnalyticsSection,
} from '../lib/constants'
import type { Session } from '../lib/identity'
import { DashboardScreen } from './DashboardScreen'
import { ReportingScreen } from './ReportingScreen'

interface Props {
  session: Session
  refreshNonce?: number
  /** Which sub-tabs this caller may open, in tab order. Defaults to both so a
      test or a future caller need not thread the session's answer through. */
  sections?: readonly AnalyticsSection[]
  /** Passed straight through to the Dashboard's read-only invoice modal —
      `App.tsx` owns the navigation, so it owns this answer. */
  resolveStep?: (invoice: Invoice) => { label: string; onGo: () => void } | null
}

export function AnalyticsScreen({
  session,
  refreshNonce,
  sections = ANALYTICS_SECTIONS,
  resolveStep,
}: Props) {
  const shown = useMemo(() => [...sections], [sections])
  const [tab, setTab] = useState<AnalyticsSection>(shown[0] ?? 'dashboard')
  // THE CRITERIA LIVE HERE, AND BOTH TABS SHARE THEM (owner, 2026-09-15:
  // "if I have a dashboard with a peculiar set of numbers, I want to be able
  // just to jump over to Reporting and click to run a corresponding report").
  //
  // They were the Dashboard's alone from 2026-09-14, held here because the
  // button that edits them sits in this screen's header rather than in
  // `DashboardScreen`. Reporting kept its own copy of the same fifteen
  // questions in its own `useState`. One question asked twice, answered twice,
  // and the two answers had no way to meet — so a reader who had narrowed the
  // Dashboard to something worth reporting on had to key it all in again.
  //
  // THREE EDITORS OF ONE STATE now: the Filters popover, a bar click on the
  // dashboard, and the Reporting form itself. Editing on any of them is visible
  // on the others, which is the point — and it is why Reporting's **Clear**
  // clears the Dashboard too. That is one filter set for the screen, not a
  // surprise: the Filters icon carries the count, so a narrowed Dashboard never
  // looks unnarrowed on the way back.
  //
  // TWO EDITORS NOW: the popover, and a bar click on the dashboard itself
  // (owner, 2026-09-15). Both write the same state through `setCriteria`, which
  // is why the bar click needs no filter machinery of its own.
  //
  // The button is moving to the app shell's own header as an icon (owner,
  // 2026-09-15). When it does, this state moves up to `App.tsx` and both
  // consumers below take it as a prop — nothing else about either changes.
  const [criteria, setCriteria] = useState<Criteria>(emptyCriteria)
  // A REFRESH OF ITS OWN (QA 2026-10-04 B-12). Analytics refreshes by hand
  // only (docs/app.md §5), and the hand had nothing to press: the Invoices
  // Refresh is not on this page, so leaving and coming back was the only way.
  // Same icon and tooltip as that one; it re-reads whichever tab is open —
  // the Dashboard's figures and list, or Reporting's recent reports.
  const [localNonce, setLocalNonce] = useState(0)
  const nonce = (refreshNonce ?? 0) + localNonce

  // If the visible set changes under us — a role revoked mid-session, a profile
  // refresh — land on a tab that still exists rather than rendering nothing.
  useEffect(() => {
    if (shown.length > 0 && !shown.includes(tab)) setTab(shown[0]!)
  }, [shown, tab])

  // THE TAB ON SCREEN IS THE LENS (D-258): every read this screen makes — an
  // invoice opened from the Dashboard, a report's pages — is scoped to that
  // tab's appointed companies. A LAYOUT effect so it is in place before the
  // tabs' own data effects fire (React runs every layout effect first), and
  // cleared on leaving so no other screen reads through it.
  useLayoutEffect(() => {
    setAnalyticsLens(tab)
    return () => setAnalyticsLens(null)
  }, [tab])

  // ONE VISIBLE TAB STILL SHOWS (owner, 2026-09-28). It used to be hidden
  // under the "no dead toggles" rule, when the page carried its own title. The
  // title is gone and the selected tab is now the page's name, so a holder of
  // `analytics_reporting` alone sees "Reporting" in the header like everyone
  // else does.
  return (
    <div>
      {/* THE FILTERS BUTTON ONLY ON THE DASHBOARD TAB — Reporting asks the
          same fifteen criteria in its own form, with a Submit, so a second copy
          of them in the header would be two answers to one question. Refresh
          is on both. ONE MOUNT AT A TIME is `ScreenActions`' contract, so the
          two share one. */}
      <ScreenActions>
        {tab === 'dashboard' && shown.includes('dashboard') && (
          <DashboardFilterButton
            value={criteria}
            onChange={setCriteria}
            companyIds={session.analyticsCompanies.dashboard}
          />
        )}
        <Tooltip content="Refresh" relationship="label">
          <Button appearance="secondary" icon={<ArrowClockwiseRegular />} onClick={() => setLocalNonce((n) => n + 1)} />
        </Tooltip>
      </ScreenActions>
      <PageTabs
        tabs={shown.map((section) => ({ value: section, label: ANALYTICS_SECTION_LABEL[section] }))}
        selectedValue={tab}
        onSelect={setTab}
        ariaLabel="Analytics sections"
      />
      <ScreenIntro>{ANALYTICS_SECTION_SUBHEADER[tab]}</ScreenIntro>

      {tab === 'dashboard' && shown.includes('dashboard') && (
        <DashboardScreen
          session={session}
          refreshNonce={nonce}
          criteria={criteria}
          onCriteriaChange={setCriteria}
          resolveStep={resolveStep}
        />
      )}
      {tab === 'reporting' && shown.includes('reporting') && (
        <ReportingScreen
          session={session}
          refreshNonce={nonce}
          criteria={criteria}
          onCriteriaChange={setCriteria}
        />
      )}
    </div>
  )
}
